import type { UseQueryOptions } from "@tanstack/react-query";
import { useMemo } from "react";

import { useDisclosure } from "@equidefi/hooks";

import { useGetSigningPartyToken, useGetUserToken } from "hooks/useSignatures";

export const useDocusealCurrentUser = (investmentAgreementId: string, options: UseQueryOptions = {}) => {
  return useDocusealSign(investmentAgreementId, undefined, options);
};

export const useDocusealSigningParty = (
  investmentAgreementId: string,
  signingPartyId: string,
  options: UseQueryOptions = {},
) => {
  return useDocusealSign(investmentAgreementId, signingPartyId, options);
};

export const useDocusealSign = (
  investmentAgreementId: string,
  signingPartyId?: string,
  options: UseQueryOptions = {},
) => {
  const overlay = useDisclosure();
  const opts = useMemo(
    () => ({
      ...options,
      enabled: !!investmentAgreementId && (options?.enabled ?? true),
    }),
    [investmentAgreementId, options],
  );

  const useHook = useMemo(
    () =>
      signingPartyId
        ? () => useGetSigningPartyToken(investmentAgreementId, signingPartyId, opts)
        : () => useGetUserToken(investmentAgreementId, opts),
    [investmentAgreementId, signingPartyId, opts],
  );

  // Fetch user and needed Docuseal tokens
  const hook = useHook();

  const extras = useMemo(
    () => ({
      url: hook?.data ? `https://docuseal.com/s/${hook.data?.submitter?.slug}` : undefined,
      name: `Investment Agreement - ${hook?.data?.submitter?.name}`,
      role: hook?.data?.submitter?.role,
      token: hook?.data?.token,
    }),
    [hook?.data],
  );

  return {
    ...hook,
    ...extras,
    overlay,
  };
};
